import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { DashboardLayout } from '../components/DashboardLayout';
import { api } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { ASSET_CATEGORIES, getCategoryColor } from '../utils/helpers';
import { Sparkles, Send, Loader2, Car, Palette, Wine, Gem, ShoppingBag, Watch, Lock, Crown, History, ChevronDown, ChevronUp, Plus, X, Wrench, ShieldAlert, CalendarClock, TrendingUp, FolderLock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const iconMap = {
  car: Car,
  art: Palette,
  wine: Wine,
  jewelry: Gem,
  handbag: ShoppingBag,
  watch: Watch,
};

  // Replaced by context-aware quickPrompts from /api/insights/quick-prompts

const PROMPT_ICON = {
  'cover-uninsured': ShieldAlert,
  'summarise-underinsurance': ShieldAlert,
  'draft-renewal': CalendarClock,
  'concentration': TrendingUp,
  'vault-gaps': FolderLock,
  'rebalance': Sparkles,
};

const PROMPT_TONE_CLASSES = {
  red:    'border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/10 hover:border-red-500/50',
  amber:  'border-amber-500/30 bg-amber-500/5 text-amber-200 hover:bg-amber-500/10 hover:border-amber-500/50',
  gold:   'border-gold/30 bg-gold/5 text-gold hover:bg-gold/10 hover:border-gold/50',
  muted:  'border-[#262626] bg-[#0A0A0A] text-zinc-300 hover:border-white/20 hover:text-white',
};

export default function Insights() {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [fetchingSummary, setFetchingSummary] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [quickPrompts, setQuickPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);

  useEffect(() => {
    checkAccess();
    fetchSummary();
    loadConversationHistory();
    fetchQuickPrompts();
  }, []);

  const fetchQuickPrompts = async () => {
    try {
      const r = await api.getQuickPrompts();
      setQuickPrompts(r.data?.prompts || []);
    } catch {
      setQuickPrompts([]);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const checkAccess = async () => {
    try {
      const response = await api.getSubscriptionAccess();
      const hasAccess = response.data?.access?.ai_insights;
      setAccessDenied(!hasAccess);
    } catch (error) {
      console.error('Failed to check access:', error);
      setAccessDenied(true);
    } finally {
      setCheckingAccess(false);
    }
  };

  const loadConversationHistory = () => {
    // Load from localStorage for now
    const saved = localStorage.getItem('assagart_ai_history');
    if (saved) {
      try {
        setConversationHistory(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load history:', e);
      }
    }
  };

  const saveToHistory = (question, response) => {
    const newEntry = {
      id: Date.now(),
      question,
      response,
      timestamp: new Date().toISOString()
    };
    const updated = [newEntry, ...conversationHistory].slice(0, 50); // Keep last 50
    setConversationHistory(updated);
    localStorage.setItem('assagart_ai_history', JSON.stringify(updated));
  };

  const deleteHistoryEntry = (id) => {
    const updated = conversationHistory.filter(e => e.id !== id);
    setConversationHistory(updated);
    localStorage.setItem('assagart_ai_history', JSON.stringify(updated));
  };

  const clearAllHistory = () => {
    setConversationHistory([]);
    localStorage.removeItem('assagart_ai_history');
  };

  const startNewConversation = () => {
    setQuery('');
    setInsight(null);
  };

  const groupHistoryByDate = (entries) => {
    const groups = { today: [], yesterday: [], thisWeek: [], earlier: [] };
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfWeek = new Date(startOfToday); startOfWeek.setDate(startOfWeek.getDate() - 7);
    entries.forEach(e => {
      const ts = new Date(e.timestamp);
      if (ts >= startOfToday) groups.today.push(e);
      else if (ts >= startOfYesterday) groups.yesterday.push(e);
      else if (ts >= startOfWeek) groups.thisWeek.push(e);
      else groups.earlier.push(e);
    });
    return groups;
  };

  const fetchSummary = async () => {
    try {
      const response = await api.getPortfolioSummary();
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setFetchingSummary(false);
    }
  };

  const handleGetInsights = async (customQuery) => {
    const queryToUse = customQuery || query;
    if (!queryToUse.trim()) {
      toast.error('Please enter a question');
      return;
    }

    setLoading(true);
    try {
      const response = await api.getInsights(queryToUse);
      setInsight(response.data);
      setAccessDenied(false);
      if (customQuery) setQuery(customQuery);
      
      // Save to history
      saveToHistory(queryToUse, response.data.insight);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to get insights');
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingAccess) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="insights-loading">
          <Skeleton className="h-3 w-32 mb-3 bg-gold/10" />
          <Skeleton className="h-9 w-64 mb-8 bg-white/5" />
          <Skeleton className="h-96 bg-white/5" />
        </div>
      </DashboardLayout>
    );
  }

  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="insights-page">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles size={24} className="text-gold" />
              <p className="text-gold uppercase tracking-[0.2em] text-xs">AI-Powered</p>
            </div>
            <h1 className="font-serif text-3xl text-white">Portfolio Insights</h1>
          </div>

          {/* Upgrade Required Card */}
          <div className="luxury-card p-8 text-center">
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock size={32} className="text-gold" />
            </div>
            <h2 className="font-serif text-2xl text-white mb-3">AI Insights</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              AI-powered portfolio analysis and personalised recommendations require a Professional or Elite membership.
              Upgrade now to unlock intelligent insights for your luxury asset collection.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button className="btn-primary flex items-center gap-2">
                  <Crown size={16} />
                  Upgrade Membership
                </Button>
              </Link>
            </div>
            
            {/* Preview of what they're missing */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4">What you'll unlock</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-xl mx-auto">
                <div className="p-3 bg-[#0A0A0A] border border-white/5 rounded">
                  <p className="text-sm text-white">Portfolio Analysis</p>
                  <p className="text-xs text-muted-foreground">AI-driven recommendations</p>
                </div>
                <div className="p-3 bg-[#0A0A0A] border border-white/5 rounded">
                  <p className="text-sm text-white">Market Trends</p>
                  <p className="text-xs text-muted-foreground">Predictive insights</p>
                </div>
                <div className="p-3 bg-[#0A0A0A] border border-white/5 rounded">
                  <p className="text-sm text-white">Sell Timing</p>
                  <p className="text-xs text-muted-foreground">Optimal exit strategies</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 max-w-[1500px] mx-auto" data-testid="insights-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles size={24} className="text-gold" />
            <p className="text-gold uppercase tracking-[0.2em] text-xs">AI-Powered</p>
          </div>
          <h1 className="font-serif text-3xl text-white">What should I do next?</h1>
          <p className="text-muted-foreground mt-2">
            Action-oriented advice rooted in what's actually in your vault. Pick a suggestion below or ask anything.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Left: History Sidebar (ChatGPT-style) */}
          <aside className="hidden lg:flex lg:col-span-3 flex-col bg-[#080808] border border-[#1A1A1A] rounded-md overflow-hidden h-[calc(100vh-220px)] sticky top-24" data-testid="ai-history-sidebar">
            <div className="p-3 border-b border-[#1A1A1A] space-y-2">
              <Button onClick={startNewConversation} className="w-full bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 justify-start gap-2 h-9" data-testid="new-conversation-btn">
                <Plus size={14} /> New conversation
              </Button>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-1">Conversation history</p>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              {conversationHistory.length === 0 ? (
                <p className="px-4 py-6 text-xs text-muted-foreground italic">Your previous questions will appear here.</p>
              ) : (
                (() => {
                  const groups = groupHistoryByDate(conversationHistory);
                  const sections = [
                    { label: 'Today', items: groups.today },
                    { label: 'Yesterday', items: groups.yesterday },
                    { label: 'Previous 7 days', items: groups.thisWeek },
                    { label: 'Earlier', items: groups.earlier },
                  ].filter(s => s.items.length);
                  return sections.map(section => (
                    <div key={section.label} className="mb-3">
                      <p className="px-3 py-1 text-[10px] text-muted-foreground/70 uppercase tracking-wider">{section.label}</p>
                      {section.items.map(entry => {
                        const active = insight && insight.insight === entry.response;
                        return (
                          <div key={entry.id} className={`group relative mx-1 mb-0.5 rounded transition-colors ${active ? 'bg-gold/10' : 'hover:bg-white/5'}`} data-testid={`history-${entry.id}`}>
                            <button
                              onClick={() => {
                                setQuery(entry.question);
                                setInsight({ insight: entry.response, generated_at: entry.timestamp });
                              }}
                              className="w-full text-left px-3 py-2 pr-8"
                            >
                              <p className={`text-xs truncate ${active ? 'text-gold' : 'text-white'}`}>{entry.question}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteHistoryEntry(entry.id); }}
                              className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Delete entry"
                              data-testid={`delete-history-${entry.id}`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()
              )}
            </div>

            {conversationHistory.length > 0 && (
              <button
                onClick={() => { if (window.confirm('Clear all conversation history?')) clearAllHistory(); }}
                className="border-t border-[#1A1A1A] py-2 text-[11px] text-muted-foreground hover:text-red-400 transition-colors"
                data-testid="clear-history-btn"
              >
                Clear all
              </button>
            )}
          </aside>

          {/* Mobile: collapsible history pill */}
          <div className="lg:hidden col-span-1">
            {conversationHistory.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="w-full flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded text-xs text-muted-foreground"
                data-testid="mobile-history-toggle"
              >
                <span className="flex items-center gap-2"><History size={14} className="text-gold" /> {conversationHistory.length} previous questions</span>
                {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
            {showHistory && (
              <div className="mt-2 max-h-60 overflow-y-auto bg-[#0A0A0A] border border-[#1A1A1A] rounded">
                {conversationHistory.map(entry => (
                  <button key={entry.id} onClick={() => { setQuery(entry.question); setInsight({ insight: entry.response, generated_at: entry.timestamp }); setShowHistory(false); }}
                    className="w-full p-3 text-left hover:bg-white/5 border-b border-[#1A1A1A] last:border-0">
                    <p className="text-xs text-white truncate">{entry.question}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Center: Main Content */}
          <div className="lg:col-span-6 space-y-6">
            {/* Query Input */}
            <div className="luxury-card p-6">
              <h2 className="font-serif text-lg text-white mb-2">Tell me what to do</h2>
              <p className="text-xs text-muted-foreground mb-4">Pick an action prompt below, or write your own question.</p>

              {/* Action prompt cards - context-aware, replaces the old generic suggested queries */}
              {!loadingPrompts && quickPrompts.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="quick-prompts">
                  {quickPrompts.map(p => {
                    const Icon = PROMPT_ICON[p.id] || Sparkles;
                    const cls = PROMPT_TONE_CLASSES[p.tone] || PROMPT_TONE_CLASSES.muted;
                    const handleDeepLink = (e) => {
                      e.stopPropagation();
                      if (!p.deep_link) return;
                      const qs = new URLSearchParams(p.deep_link.query || {}).toString();
                      navigate(qs ? `${p.deep_link.path}?${qs}` : p.deep_link.path);
                    };
                    return (
                      <div
                        key={p.id}
                        data-testid={`quick-prompt-${p.id}`}
                        className={`group relative flex items-center gap-2.5 p-3 rounded border transition-colors ${cls} ${loading ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <button
                          type="button"
                          onClick={() => handleGetInsights(p.prompt)}
                          disabled={loading}
                          className="flex items-center gap-2.5 flex-1 text-left min-w-0"
                          data-testid={`quick-prompt-ask-${p.id}`}
                        >
                          <Icon size={14} className="flex-shrink-0" />
                          <span className="text-xs leading-snug flex-1 truncate">{p.label}</span>
                        </button>
                        {p.deep_link && (
                          <button
                            type="button"
                            onClick={handleDeepLink}
                            title={p.deep_link.label}
                            aria-label={p.deep_link.label}
                            data-testid={`quick-prompt-link-${p.id}`}
                            className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors opacity-70 hover:opacity-100"
                          >
                            <ArrowRight size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="space-y-3">
                <Textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. Should I liquidate my Ferrari? Draft my Aon renewal letter. Summarise my underinsurance gaps."
                  className="luxury-input min-h-[100px]"
                  data-testid="insights-query-input"
                />
                <Button 
                  onClick={() => handleGetInsights()}
                  disabled={loading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="get-insights-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Thinking...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Get my next action
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Insight Response */}
            {insight && (
              <div className="luxury-card p-6 animate-fade-in" data-testid="insight-response">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={18} className="text-gold" />
                  <h2 className="font-serif text-lg text-white">AI Analysis</h2>
                </div>
                <div className="prose prose-invert max-w-none ai-response">
                  <ReactMarkdown
                    components={{
                      p: ({children}) => <p className="text-[#C0C0C0] leading-relaxed mb-4">{children}</p>,
                      strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                      em: ({children}) => <em className="text-gold italic">{children}</em>,
                      ul: ({children}) => <ul className="list-disc list-inside space-y-2 mb-4 text-[#C0C0C0]">{children}</ul>,
                      ol: ({children}) => <ol className="list-decimal list-inside space-y-2 mb-4 text-[#C0C0C0]">{children}</ol>,
                      li: ({children}) => <li className="text-[#C0C0C0]">{children}</li>,
                      h1: ({children}) => <h1 className="text-xl font-serif text-white mb-3 mt-4">{children}</h1>,
                      h2: ({children}) => <h2 className="text-lg font-serif text-white mb-2 mt-4">{children}</h2>,
                      h3: ({children}) => <h3 className="text-base font-medium text-white mb-2 mt-3">{children}</h3>,
                      code: ({children}) => <code className="bg-[#1A1A1A] text-gold px-1.5 py-0.5 rounded text-sm">{children}</code>,
                      blockquote: ({children}) => <blockquote className="border-l-2 border-gold pl-4 italic text-[#A0A0A0] my-4">{children}</blockquote>,
                    }}
                  >
                    {insight.insight}
                  </ReactMarkdown>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Generated {new Date(insight.generated_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Right: Portfolio Summary */}
          <div className="lg:col-span-3 space-y-6">
            <div className="luxury-card p-6">
              <h2 className="font-serif text-lg text-white mb-4">Portfolio Summary</h2>
              
              {fetchingSummary ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gold animate-spin" />
                </div>
              ) : summary ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Value</p>
                    <p className="text-2xl text-gold font-serif">{formatCurrency(summary.total_value)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Assets</p>
                    <p className="text-xl text-white">{summary.total_assets}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Overall Return</p>
                    <p className={`text-xl ${summary.gain_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {summary.gain_percentage >= 0 ? '+' : ''}{summary.gain_percentage}%
                    </p>
                  </div>

                  {/* Category Breakdown */}
                  {summary.category_breakdown && Object.keys(summary.category_breakdown).length > 0 && (
                    <div className="pt-4 border-t border-[#1A1A1A]">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">By Category</p>
                      <div className="space-y-2">
                        {Object.entries(summary.category_breakdown).map(([cat, data]) => {
                          const Icon = iconMap[cat];
                          const catInfo = ASSET_CATEGORIES.find(c => c.value === cat);
                          return (
                            <div key={cat} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {Icon && <Icon size={14} className={getCategoryColor(cat)} />}
                                <span className="text-sm text-muted-foreground">{catInfo?.label || cat}</span>
                              </div>
                              <span className="text-sm text-white">{formatCurrency(data.value)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No portfolio data</p>
              )}
            </div>

            {/* AI Info */}
            <div className="luxury-card p-6">
              <h3 className="text-sm text-white mb-2">Powered by AI</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Our AI analyses your portfolio composition, market trends, and historical data 
                to provide personalised insights and recommendations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
